import { useState, useMemo } from 'react';

/**
 * Client-side sort hook.
 * Usage:
 *   const { sorted, sortKey, sortDir, onSort } = useSortable(data, 'name');
 *   // replace `data.map(...)` with `sorted.map(...)`
 *   // pass { sortKey, sortDir, onSort } into <SortableTh>
 */
export function useSortable(data, defaultKey = '', defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !data?.length) return data || [];
    return [...data].sort((a, b) => {
      let av = a[sortKey] ?? '';
      let bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      if (typeof av === 'boolean') {
        return sortDir === 'asc' ? (bv === av ? 0 : av ? -1 : 1) : (bv === av ? 0 : av ? 1 : -1);
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, onSort };
}
